/**
 * Helpers for Storyblok asset URLs.
 *
 * Storyblok encodes an asset's intrinsic pixel dimensions directly in its URL:
 *   https://a.storyblok.com/f/255103/414x800/a31fb04749/grogu_hero_cover_mobile.avif
 *                                    ^^^^^^^
 * That means we can always recover width/height for an <img>/<video poster> without
 * depending on the CMS editor having filled in the optional width/height fields, which
 * in practice are frequently absent (and their absence is what leaves elements unsized
 * and shifting layout once the real asset loads).
 */

const STORYBLOK_DIMENSIONS_RE = /\/(\d+)x(\d+)\//;

/**
 * Reads an asset's intrinsic dimensions out of its Storyblok URL.
 * Returns null for non-Storyblok URLs, SVG-style `0x0` paths, or anything unparseable.
 */
export const getStoryblokAssetDimensions = (filename) => {
  if (!filename || typeof filename !== 'string') return null;

  const match = filename.match(STORYBLOK_DIMENSIONS_RE);
  if (!match) return null;

  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!width || !height) return null;

  return { width, height };
};

/**
 * Returns a `width / height` string suitable for the CSS `aspect-ratio` property,
 * preferring explicit CMS values and falling back to the URL-encoded dimensions.
 */
export const getStoryblokAspectRatio = (asset) => {
  if (!asset) return undefined;

  if (asset.width && asset.height) {
    return `${asset.width} / ${asset.height}`;
  }

  const dimensions = getStoryblokAssetDimensions(asset.filename);
  return dimensions ? `${dimensions.width} / ${dimensions.height}` : undefined;
};

/**
 * Builds a resized/recompressed Storyblok image URL via their image service.
 *
 * Storyblok serves the original asset at full quality when no `/m/` transform is present,
 * which is how a 414x800 poster ends up costing 140 KiB. Passing it through the image
 * service with an explicit width and quality is a large, lossless-looking saving.
 *
 * Passing `height: 0` (the default) preserves the aspect ratio.
 */
export const getStoryblokImageUrl = (filename, { width, height = 0, quality = 70 } = {}) => {
  if (!filename || typeof filename !== 'string') return filename;

  // Already transformed — don't stack `/m/` segments.
  if (filename.includes('/m/')) return filename;

  const intrinsic = getStoryblokAssetDimensions(filename);
  // Never upscale: Storyblok will happily render a 1272px source at 1920px, producing a
  // bigger file with no extra detail.
  const targetWidth = intrinsic?.width ? Math.min(width || intrinsic.width, intrinsic.width) : width;

  if (!targetWidth) return `${filename}/m/filters:quality(${quality})`;

  return `${filename}/m/${targetWidth}x${height}/filters:quality(${quality})`;
};
